import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { CommsBackend } from '../lib/config';
import { useFindOrCreateConversation } from './useFindOrCreateConversation';

interface WorkerBatchResponse {
  success: boolean;
  data: Array<{ id: number; comms_backend?: CommsBackend }>;
}

/**
 * Routes the admin "Message Worker" action to the worker's actual comms
 * backend. Supabase-cohort workers run the Supabase-only stack, so opening a
 * PubNub ChatGroup for them silently drops the message — their app never
 * subscribes to PubNub. This resolves the worker's backend from graphy and,
 * for Supabase workers, opens the Supabase conversation instead.
 *
 * Fail-safe: any resolve error falls back to PubNub (returns `false`) so a
 * graphy hiccup never routes to a Supabase surface the worker's app may not
 * mount.
 *
 * `openChatV2IfCohort` returns `true` when it opened a Supabase
 * conversation (the caller should stop) and `false` when the worker is on
 * PubNub (the caller runs its existing PubNub path).
 */
export function useWorkerChatRouter() {
  const { open: openSupabaseConversation } = useFindOrCreateConversation();

  const openChatV2IfCohort = useCallback(
    async (params: { workerId: number; name: string }): Promise<boolean> => {
      const { workerId, name } = params;

      let backend: CommsBackend = 'pubnub';
      try {
        const res = await axiosRubyClient.get<WorkerBatchResponse>(
          '/api/admin/workers/batch',
          { params: { ids: [workerId] } },
        );
        const chip = res.data.data?.find((w) => w.id === workerId);
        backend = chip?.comms_backend === 'supabase' ? 'supabase' : 'pubnub';
      } catch (err: unknown) {
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'resolve_worker_backend' },
          extra: { worker_id: workerId },
        });
        return false;
      }

      if (backend !== 'supabase') return false;

      const conversationId = await openSupabaseConversation(workerId, name);
      // /chat-v2 is the only surface that reads conversation_id
      // (CommsAgencyChat); /inappchat is hard-wired to the PubNub inbox.
      window.open(`/chat-v2?conversation_id=${conversationId}`, '_blank');
      return true;
    },
    [openSupabaseConversation],
  );

  return { openChatV2IfCohort };
}
